import React, { useId } from 'react';
import Modal3D from '../ui/Modal3D';
import Button3D from '../ui/Button3D';
import SegmentedSwitch3D from '../ui/SegmentedSwitch3D';

// Display & Theme: a per-USER light/dark/auto preference (saved to this browser).
//
// BL-177 Step 2j (E): this is a Settings modal now, so it is built on the shared 3D modal (ui/Modal3D) with the 3D
// segmented switch instead of the old tabbed full-size dialog. Behaviour is unchanged: the same settings object goes in and the
// same changed copy comes out through onSettingsChange (theme 'light' | 'dark' | 'auto', autoTheme.mode 'system' | 'time',
// autoTheme.timeBasedSchedule.darkStart / darkEnd), applied the moment a choice is made.
//
// The "Custom Colors" tab was DELETED earlier (Val, 2026-07-17). It let a user pick primary / secondary / etc. colours and said
// "automatically saved" but nothing in the app ever read `customTheme`; a user's display preference must never repaint the
// company's brand. Brand colour is per-ENTITY, in the database, and lives in Settings › Organization › Branding.
const THEME_OPTIONS = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'auto', label: 'Auto' },
];
const AUTO_OPTIONS = [
    { value: 'system', label: 'Match my device' },
    { value: 'time', label: 'By time of day' },
];

function DisplayThemeModal({ onClose, settings, onSettingsChange, portal = true, surfaceTheme = 'light' }) {
    const ids = useId();
    const theme = settings?.theme === 'dark' || settings?.theme === 'auto' ? settings.theme : 'light';
    const autoTheme = settings?.autoTheme || {};
    const autoMode = autoTheme.mode === 'time' ? 'time' : 'system';
    const schedule = autoTheme.timeBasedSchedule || {};

    const chooseTheme = (next) => {
        if (next === 'auto') {
            onSettingsChange({ ...settings, theme: 'auto', autoTheme: { ...autoTheme, enabled: true } });
            return;
        }
        onSettingsChange({ ...settings, theme: next });
    };
    const chooseAutoMode = (mode) => {
        onSettingsChange({ ...settings, theme: 'auto', autoTheme: { ...autoTheme, enabled: true, mode } });
    };
    const setScheduleTime = (field, value) => {
        onSettingsChange({
            ...settings,
            autoTheme: { ...autoTheme, timeBasedSchedule: { ...schedule, [field]: value } },
        });
    };

    return (
        <Modal3D
            className="ia-settings-popup"
            portal={portal}
            size="wide"
            title="Display & theme"
            subtitle="Light, dark or automatic, for this browser only."
            onClose={onClose}
            testId="display-theme-modal"
            theme={surfaceTheme}
            actions={<Button3D variant="go" onClick={onClose}>Done</Button3D>}
        >
            <div className="flex flex-col gap-4 text-sm">
                <div className="flex flex-col gap-2">
                    <p id={`${ids}-theme`} className="font-semibold">Application theme</p>
                    <SegmentedSwitch3D labelledBy={`${ids}-theme`} options={THEME_OPTIONS} value={theme} onChange={chooseTheme} testId="theme-switch" />
                </div>

                {theme === 'auto' && (
                    <div className="flex flex-col gap-3" data-testid="auto-theme-section">
                        <div className="flex flex-col gap-2">
                            <p id={`${ids}-auto`} className="font-semibold">Switch automatically</p>
                            <SegmentedSwitch3D labelledBy={`${ids}-auto`} options={AUTO_OPTIONS} value={autoMode} onChange={chooseAutoMode} testId="auto-mode-switch" />
                        </div>
                        {autoMode === 'time' ? (
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="auto-theme-schedule">
                                <label className="flex flex-col gap-1 font-semibold" htmlFor={`${ids}-dark-start`}>
                                    Dark mode starts
                                    <input
                                        id={`${ids}-dark-start`}
                                        type="time"
                                        value={schedule.darkStart || '18:00'}
                                        onChange={(event) => setScheduleTime('darkStart', event.target.value)}
                                        className="min-h-[36px] rounded-lg border border-[#64748B] bg-[#0B1220] px-2 text-white"
                                    />
                                    <span className="text-xs font-normal">Evening (default 6:00 PM)</span>
                                </label>
                                <label className="flex flex-col gap-1 font-semibold" htmlFor={`${ids}-dark-end`}>
                                    Light mode starts
                                    <input
                                        id={`${ids}-dark-end`}
                                        type="time"
                                        value={schedule.darkEnd || '06:00'}
                                        onChange={(event) => setScheduleTime('darkEnd', event.target.value)}
                                        className="min-h-[36px] rounded-lg border border-[#64748B] bg-[#0B1220] px-2 text-white"
                                    />
                                    <span className="text-xs font-normal">Morning (default 6:00 AM)</span>
                                </label>
                            </div>
                        ) : null}
                        <p>
                            {autoMode === 'system'
                                ? 'IntelAgent switches to match your operating system, the moment your device changes.'
                                : 'IntelAgent uses dark mode in the evening and light mode in the daytime, between the times above.'}
                        </p>
                    </div>
                )}

                <p data-testid="display-theme-note">
                    Your choice is applied immediately and saved for this browser. It is your own preference: it does not change what anyone
                    else sees, and it never repaints your company&apos;s brand colour (that lives in Admin Center, Organization, Branding).
                </p>
            </div>
        </Modal3D>
    );
}

export default DisplayThemeModal;
